import { TYPES } from './constants';
import { renderContent } from './node';
import { parseBoxShadowValue } from './effects/shadow';
import { getFontSetting, wrapText } from './text';
import { resolveRadius } from './utils/radius';
import {
  createRasterContext,
  getCacheByType,
  hasDynamicNodeInSubtree,
  isHighRasterCost,
} from './utils/raster';
import { SCALING_ZOOM_TOLERANCE_MIN, SCALING_ZOOM_TOLERANCE_MAX } from './utils/render-cache';
import { getLineHeightInPx } from './utils/text';
import { isTextSupported } from './utils/wrap-text';

export function renderGroup(stageContext, node) {
  if (node.type !== TYPES.group) return;
  const renderNodes = stageContext.api.getChildren(node.id);

  // Check if we should use caching
  const hasDynamicNode = hasDynamicNodeInSubtree(stageContext, node);
  const tooFewChildren = renderNodes.length < 2;
  const highCost = isHighRasterCost(stageContext, { w: node.w, h: node.h });
  const isScaling = !!stageContext.providers?.scale?.scaling;
  // Avoid caching while actively scaling to prevent blur; cache will be rebuilt after scale settles
  const shouldCache =
    !isScaling && !hasDynamicNode && !tooFewChildren && !highCost && stageContext.mode !== 'export';

  if (shouldCache) {
    renderGroupWithCache(stageContext, node, renderNodes);
  } else {
    renderGroupDirect(stageContext, node, renderNodes);
  }
}

function renderGroupWithCache(stageContext, node, renderNodes) {
  const { context } = stageContext;
  const cacheName = node.id;
  const caches = getCacheByType(stageContext, 'renderGroup');
  const existCache = caches.get(cacheName);
  const { scale, dirty: dirtyProvider } = stageContext.providers || {};

  // Improved DPR calculation for high-resolution rendering
  const deviceDPR = stageContext.dpr || 1;
  const safeDPR = Math.max(2, deviceDPR); // Ensure minimum DPR of 2 for crispness
  const zoom = (scale?.scale || 1) * safeDPR;

  // Strict cache matching conditions to prevent blur during zoom
  // Use relative zoom tolerance instead of absolute to handle different zoom levels better
  const zoomRatio = existCache ? existCache.zoom / zoom : 0;
  // Cache is valid if zoom ratio is within 5% (0.95 to 1.05)
  // This prevents reusing low-resolution caches when zooming in
  const isZoomMatch = existCache && zoomRatio >= 0.95 && zoomRatio <= 1.05;

  // 使用细粒度的脏检测 - 获取所有后代节点的版本
  const descendants = stageContext.api?.getDescendants
    ? stageContext.api.getDescendants(node.id)
    : renderNodes;
  const descendantIds = descendants.map((n) => n.id);
  const subtreeVersion = dirtyProvider.getSubtreeVersion(node.id, descendantIds);
  const groupContentKey = dirtyProvider.getContentKey(node.id);
  const groupTransformKey = dirtyProvider.getTransformKey(node.id);

  // 检测是否正在缩放
  const isScaling = !!stageContext.providers?.scale?.scaling;

  // 缩放时的宽松容差：缩放比在 0.5x - 2x 范围内可复用缓存
  const scalingZoomRatio = existCache ? existCache.zoom / zoom : 0;
  const isZoomInScalingRange =
    existCache &&
    scalingZoomRatio >= SCALING_ZOOM_TOLERANCE_MIN &&
    scalingZoomRatio <= SCALING_ZOOM_TOLERANCE_MAX;

  // 缩放时检查是否有可用的旧缓存（检查内容 + zoom 在宽松范围内）
  const isCacheValidForScaling =
    existCache &&
    existCache.subtreeVersion === subtreeVersion &&
    existCache.groupContentKey === groupContentKey &&
    existCache.canvas &&
    existCache.renderConfig &&
    existCache.renderConfig.actualWidth > 0 &&
    existCache.renderConfig.actualHeight > 0 &&
    isZoomInScalingRange;

  // 完整缓存有效性检查（非缩放状态）
  const isCacheValid =
    existCache &&
    existCache.subtreeVersion === subtreeVersion &&
    existCache.groupContentKey === groupContentKey &&
    existCache.canvas &&
    existCache.renderConfig &&
    existCache.renderConfig.actualWidth > 0 &&
    existCache.renderConfig.actualHeight > 0 &&
    isZoomMatch &&
    !isScaling;

  // ========== 缩放时使用旧缓存（避免重新渲染） ==========
  if (isScaling && isCacheValidForScaling) {
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'low';

    // 绘制缓存到节点的实际位置和尺寸（保持位置一致，避免漂移）
    context.drawImage(
      existCache.canvas,
      0,
      0,
      existCache.renderConfig.actualWidth,
      existCache.renderConfig.actualHeight,
      node.x,
      node.y,
      node.w,
      node.h
    );

    return;
  }

  // ========== 缓存命中（非缩放状态） ==========
  if (isCacheValid) {
    if (stageContext.highQuality) {
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
    }

    // 计算位置偏移：当前位置 - 缓存时的位置
    const deltaX = node.x - existCache.nodeX;
    const deltaY = node.y - existCache.nodeY;

    context.drawImage(
      existCache.canvas,
      0,
      0,
      existCache.renderConfig.actualWidth,
      existCache.renderConfig.actualHeight,
      existCache.x + deltaX,
      existCache.y + deltaY,
      existCache.w,
      existCache.h
    );

    // 如果只是位置变化，更新缓存中的位置信息
    if (existCache.groupTransformKey !== groupTransformKey) {
      existCache.nodeX = node.x;
      existCache.nodeY = node.y;
      existCache.x += deltaX;
      existCache.y += deltaY;
      existCache.groupTransformKey = groupTransformKey;
    }

    return;
  }

  // ========== 缓存未命中，重新渲染 ==========
  // 注意：缩放时如果有旧缓存，上面已经处理了
  if (!isScaling && existCache && existCache.canvas) {
    existCache.canvas.release();
  }

  const cache = {
    subtreeVersion,
    groupContentKey,
    groupTransformKey,
    zoom,
    canvas: null,
    nodeX: node.x,
    nodeY: node.y,
    x: node.x,
    y: node.y,
    w: node.w,
    h: node.h,
    dispose: () => {
      cache.canvas?.release();
    },
  };

  // Compute expanded bounds to include overflowing children (text, nested groups)
  const expanded = computeGroupExpandedBounds(stageContext, node, renderNodes);

  // Create raster using expanded bounds
  const rasterNode = { x: expanded.x, y: expanded.y, w: expanded.w, h: expanded.h };
  const {
    canvas,
    context: cacheContext,
    renderConfig,
  } = createRasterContext(stageContext, rasterNode, zoom);
  cache.x = rasterNode.x;
  cache.y = rasterNode.y;
  cache.w = rasterNode.w;
  cache.h = rasterNode.h;
  cache.canvas = canvas;
  cache.renderConfig = renderConfig;

  // Save original transform state
  cacheContext.save();

  // Set high quality rendering parameters
  if (stageContext.highQuality) {
    cacheContext.imageSmoothingEnabled = true;
    cacheContext.imageSmoothingQuality = 'high';
  }

  const fakeStageContext = {
    ...stageContext,
    // Disable viewport culling while rasterizing cache so all children are drawn
    isNodeInViewport: () => true,
    context: cacheContext,
    _renderingToCache: true,
    renderConfig,
    _rasterOrigin: { x: rasterNode.x, y: rasterNode.y },
    _rasterWorldToCacheOffset: { x: 0, y: 0 },
  };

  // Render group content to cache (apply local translation on cache context)
  renderGroupContent(fakeStageContext, node, renderNodes);

  // Restore transform state
  cacheContext.restore();

  caches.set(cacheName, cache);

  // Draw cached content to main canvas with high quality
  if (stageContext.highQuality) {
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
  }

  // Use nearest-neighbor upscaling avoidance by drawing at integer coords when possible
  const dx = Math.round(cache.x);
  const dy = Math.round(cache.y);
  const dw = Math.round(cache.w);
  const dh = Math.round(cache.h);
  context.drawImage(
    cache.canvas,
    0,
    0,
    renderConfig.actualWidth,
    renderConfig.actualHeight,
    dx,
    dy,
    dw,
    dh
  );
}

function renderGroupDirect(stageContext, node, renderNodes) {
  const { context } = stageContext;

  context.save();
  renderGroupContent(stageContext, node, renderNodes);
  context.restore();
}

export function renderGroupContent(stageContext, node, renderNodes) {
  if (node.type !== TYPES.group) return;
  const { context } = stageContext;
  const { topLeft, topRight, bottomRight, bottomLeft, isEmpty } = resolveRadius(
    node.w,
    node.h,
    node.radius
  );

  // Apply clipping only if explicitly requested via `overflow: 'hidden'`
  // In traditional web, border-radius does NOT clip overflow by default;
  // only `overflow: hidden` does. We mirror this: radius alone shapes the
  // group background/border, but `overflow` controls whether children are clipped.
  const shouldClip = node.overflow === 'hidden';
  context.translate(node.x, node.y);
  if (!isEmpty && shouldClip) {
    context.beginPath();
    // Draw rounded rectangle path for clipping
    context.moveTo(0 + topLeft, 0);
    context.lineTo(node.w - topRight, 0);
    context.arcTo(node.w, 0, node.w, 0 + topRight, topRight);
    context.lineTo(node.w, node.h - bottomRight);
    context.arcTo(node.w, node.h, node.w - bottomRight, node.h, bottomRight);
    context.lineTo(0 + bottomLeft, node.h);
    context.arcTo(0, node.h, 0, node.h - bottomLeft, bottomLeft);
    context.lineTo(0, 0 + topLeft);
    context.arcTo(0, 0, 0 + topLeft, 0, topLeft);
    context.closePath();
    context.clip();
  }

  renderContent(stageContext, node, renderNodes);
}

function computeGroupExpandedBounds(stageContext, groupNode, renderNodes) {
  // Only clip overflow if explicitly requested via `overflow: 'hidden'`
  // radius alone doesn't clip children (mirrors web behavior)
  const shouldClip = groupNode.overflow === 'hidden';
  if (shouldClip) {
    try {
      const { isEmpty } = resolveRadius(groupNode.w, groupNode.h, groupNode.radius);
      if (!isEmpty) {
        return { x: groupNode.x, y: groupNode.y, w: groupNode.w, h: groupNode.h };
      }
    } catch {
      // Fallback to normal flow if resolveRadius fails due to invalid dims
    }
  }

  // Base bounds start with the group box itself
  let left = groupNode.x;
  let top = groupNode.y;
  let right = groupNode.x + groupNode.w;
  let bottom = groupNode.y + groupNode.h;

  const unionRect = (x, y, w, h) => {
    left = Math.min(left, x);
    top = Math.min(top, y);
    right = Math.max(right, x + w);
    bottom = Math.max(bottom, y + h);
  };

  // Per-stage measurement cache to avoid repeated wrapText calls
  const textMeasureCache = getCacheByType(stageContext, 'groupTextMeasure');

  for (const child of renderNodes) {
    const childAbsX = groupNode.x + (child.x || 0);
    const childAbsY = groupNode.y + (child.y || 0);

    // Always union the child's own box (child may be positioned outside the group box)
    if (typeof child.w === 'number' && typeof child.h === 'number') {
      unionRect(childAbsX, childAbsY, child.w, child.h);
    }

    // Check for shadow overflow
    if (child.shadow && child.shadow !== 'none') {
      const shadows = parseBoxShadowValue(child.shadow);
      for (const shadow of shadows) {
        if (shadow) {
          const shadowLeft = childAbsX + shadow.offsetX - shadow.blur - (shadow.spread || 0);
          const shadowTop = childAbsY + shadow.offsetY - shadow.blur - (shadow.spread || 0);
          const shadowRight =
            childAbsX + (child.w || 0) + shadow.offsetX + shadow.blur + (shadow.spread || 0);
          const shadowBottom =
            childAbsY + (child.h || 0) + shadow.offsetY + shadow.blur + (shadow.spread || 0);
          unionRect(shadowLeft, shadowTop, shadowRight - shadowLeft, shadowBottom - shadowTop);
        }
      }
    }

    if (child.type === TYPES.text) {
      const textContent = child.text || '';
      if (!textContent) continue;
      // If font not ready, skip overflow measurement to avoid wrong expansion
      const isLoaded = isTextSupported(child, textContent, stageContext);
      if (!isLoaded) continue;

      // Build cache key for measurement
      const fontFamily = child.fontFamily || '';
      const fontWeight = child.fontWeight || '';
      const fontSize = child.fontSize || 12;
      const letterSpacing = child.letterSpacing || 0;
      const key = `id:${child.id}|w:${child.w}|ff:${fontFamily}|fw:${fontWeight}|fs:${fontSize}|ls:${letterSpacing}|t:${textContent}`;

      let measuredHeight;
      const cached = textMeasureCache.get(child.id);
      if (cached && cached.key === key) {
        measuredHeight = cached.height;
      } else {
        // Measure text to detect vertical overflow
        const measured = wrapText(child, textContent, stageContext);
        measuredHeight = measured.height;
        textMeasureCache.set(child.id, { key, height: measuredHeight });
      }

      const fontSettings = getFontSetting(child);
      const lineHeight = getLineHeightInPx(fontSize, fontSettings.lineHeight);
      const bleed = lineHeight * 0.5; // match text cache extra bleed

      const childH = typeof child.h === 'number' ? child.h : 0;
      const overflowDelta = Math.max(0, measuredHeight - childH);

      // Distribute overflow based on vertical align
      const align = child.alignItems || 'top';
      let overflowTop = 0;
      let overflowBottom = 0;
      if (overflowDelta > 0) {
        if (align === 'center') {
          overflowTop = overflowDelta / 2;
          overflowBottom = overflowDelta / 2;
        } else if (align === 'bottom') {
          overflowTop = overflowDelta;
        } else {
          overflowBottom = overflowDelta; // top
        }
      }

      const rectLeft = childAbsX - bleed * 0.5;
      const rectTop = childAbsY - overflowTop - bleed * 0.5;
      const rectRight = childAbsX + child.w + bleed * 0.5;
      const rectBottom = childAbsY + child.h + overflowBottom + bleed * 0.5;
      unionRect(rectLeft, rectTop, rectRight - rectLeft, rectBottom - rectTop);
    } else if (child.type === TYPES.group) {
      // If nested group is explicitly clipped, its overflow is not visible → skip deep traversal
      // Only consider clipped if `overflow: 'hidden'` AND has radius
      let childIsClipped = false;
      if (child.overflow === 'hidden') {
        try {
          const { isEmpty } = resolveRadius(child.w, child.h, child.radius);
          childIsClipped = !isEmpty;
        } catch {
          childIsClipped = false;
        }
      }

      if (!childIsClipped) {
        // Recurse into nested group to include its overflow area
        const grandchildren = stageContext.api.getChildren(child.id);
        const nestedBounds = computeGroupExpandedBounds(
          stageContext,
          { ...child, x: childAbsX, y: childAbsY },
          grandchildren
        );
        unionRect(nestedBounds.x, nestedBounds.y, nestedBounds.w, nestedBounds.h);
      }
    }
  }

  return { x: left, y: top, w: Math.max(0, right - left), h: Math.max(0, bottom - top) };
}
